import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { VoucherConstants, FormFieldConstants, HTMLConstants } from "utils/constants";
import { FormHandles } from "models/propTypes/genericFormProps";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { TranslationKeys } from "translations";
import { VoucherFormTypes } from "models/formTypes";
import { WintSuperkollAPIModelAccounting } from "wint-api-client/dist/types";
import { FormikProps } from "formik";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { CurrencyEnums, StyleEnums } from "models/enums";
import { VoucherCreateFormContainerProps } from "models/propTypes/voucherPropsTypes";
//#endregion
//#region components
import VoucherCreateForm from "./createForm";
import { LoadingWithLogo, useFloatingMessages, useMountedState } from "components/common";
import { Grid, Portal } from "@mui/material";
import { useAccountService, useVoucherService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const VoucherCreateFormContainer: React.ForwardRefRenderFunction<FormHandles, VoucherCreateFormContainerProps> = (
  props,
  ref
) => {
  const voucherService = useVoucherService();
  const accountService = useAccountService();
  const { getMounted } = useMountedState();
  const { enqueueMessage } = useFloatingMessages();

  const [loading, setLoading] = React.useState(false);
  const [cashDifference, setCashDifference] = React.useState<number>(0);
  const [accounts, setAccounts] = React.useState<WintSuperkollAPIModelAccounting.Account[]>([]);

  const footerMount = document.getElementById(HTMLConstants.DialogFooterPortalId);

  const innerRef = React.useRef<FormHandles>(null);
  React.useImperativeHandle(ref, () => (innerRef?.current ? innerRef.current : ({} as FormHandles)));

  const dimensions = useSelector((store: AppState) => store.common.dimensions.cashReconciliationDimensions ?? []);

  const getTotalAmount = (items: VoucherFormTypes.AccountItem[]) => {
    return items.reduce((acc, item) => {
      const curr = Number(item.amount ?? 0);
      return acc + (isNaN(curr) ? 0 : curr);
    }, 0);
  };

  const onTotalChange = (
    name: string,
    value: VoucherFormTypes.AccountItem[],
    formikProps: FormikProps<VoucherFormTypes.Create>
  ) => {
    const { values } = formikProps;
    const { accountItems } = values;

    setCashDifference(getTotalAmount(accountItems ?? []));
  };

  const onDimensionChange = (
    item: AutocompleteListItem | null,
    typeId: string,
    name: string,
    formikProps: FormikProps<VoucherFormTypes.Create>
  ) => {
    const values = innerRef.current?.getValues ? innerRef.current.getValues() : {};
    const dimensionsForType = dimensions?.find((d) => d.key.id === typeId)?.value.map((d) => d.id ?? "") ?? [];

    innerRef.current?.setValues &&
      innerRef.current.setValues({
        accountItems: updateDimensions(values.accountItems ?? [], dimensionsForType, String(item?.id)),
      });
  };

  const updateDimensions = (
    items: VoucherFormTypes.AccountItem[],
    dimensionIds: string[],
    selectedDimensionId: string
  ) => {
    return items.map((item, i) => {
      item.dimensions = (item.dimensions.filter((dimensionId) => !dimensionIds.includes(dimensionId)) ?? []).concat(
        selectedDimensionId
      );
      return item;
    });
  };

  const onItemAmountChange = (
    name: string,
    value: VoucherFormTypes.AccountItem[],
    formikProps: FormikProps<VoucherFormTypes.Create>
  ) => {
    const { setFieldValue } = formikProps;
    const total = getTotalAmount(value) ?? 0;

    setFieldValue(FormFieldConstants.Voucher.Create.TotalAmount, total);
  };

  React.useEffect(() => {
    props.onFormValidationChange && props.onFormValidationChange(false);
    setLoading(true);

    accountService
      .getAccounts({
        active: true,
        orderByProperty: "Number",
      } as WintSuperkollAPIModelAccounting.AccountFilter)
      .then((res) => {
        if (getMounted()) {
          setAccounts(res.items ?? []);
          props.onFormValidationChange && props.onFormValidationChange(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const initialValues = React.useMemo(
    () => ({
      filesToUpload: [],
      date: DateHelper.addDays(new Date(), -1),
      accountItems: [],
      dimensions: [],
      totalAmount: 0,
    }),
    []
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object<VoucherFormTypes.Create>({
        filesToUpload: Yup.array()
          .of(
            Yup.object<UploadedFile>({
              contentType: Yup.string(),
              fileName: Yup.string(),
              fileSize: Yup.number(),
              data: Yup.string(),
            })
          )
          .when(
            FormFieldConstants.Voucher.Create.PostWithoutImage,
            ([postWithoutImage]: [boolean], schema: Yup.StringSchema) => {
              return (!postWithoutImage && schema.min(1, TranslationKeys.Validations.Required)) || schema;
            }
          ),
        date: Yup.date()
          .typeError(TranslationKeys.Validations.InvalidDate)
          .required(TranslationKeys.Validations.Required)
          .max(new Date(), TranslationKeys.Validations.InvalidDate),
        dimensions: Yup.array(),
        accountItems: Yup.array().of(
          Yup.object<VoucherFormTypes.AccountItem>({
            templateItemId: Yup.number(),
            accountId: Yup.string().required(TranslationKeys.Validations.Required),
            accountName: Yup.string().required(TranslationKeys.Validations.Required),
            amount: Yup.number().required(TranslationKeys.Validations.Required),
            dimensions: Yup.array().of(Yup.string()),
          })
        ),
        totalAmount: Yup.number(),
      }),
    []
  );

  const handleSubmit = async (values: VoucherFormTypes.Create) => {
    const series = (await voucherService.getSeries(VoucherConstants.ZSeries)).items ?? [];

    if (series.length === 0) {
      enqueueMessage(
        `${LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Voucher.SeriesInactive, "", { serie: "Z" })}`,
        StyleEnums.DisplayType.Danger,
        true
      );
      return;
    }
    const seriesId = series[0].id;
    const voucher: WintSuperkollAPIModelAccounting.VoucherPost = {
      images: values.filesToUpload?.map((f) => ({
        contentType: f.contentType,
        data: f.data,
      })),
      bookingDate: DateHelper.resetTime(values.date) ?? new Date(),
      text: values.text,
      seriesId: seriesId,
      validateLockedSystemAccounts: true,
      transactions: values.accountItems.map(
        (t) =>
          ({
            amount: t.amount,
            accountId: t.accountId,
            dimensions: t.dimensions,
            accountNumber: accounts.find((a) => a.id === t.accountId)?.number,
          } as WintSuperkollAPIModelAccounting.TransactionPost)
      ),
    };

    return voucherService.postVoucher(voucher).catch(onVoucherPostError);
  };

  const onVoucherPostError = (errors: any) => {
    if (errors?.status === 403) {
      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Errors.NotEnoughPermissions),
        StyleEnums.DisplayType.Danger
      );
      throw errors;
    }

    if (getMounted() && errors) {
      if (errors.codes?.includes(VoucherConstants.BadSerie)) {
        enqueueMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Voucher.SeriesInactive, "", { serie: "Z" }),
          StyleEnums.DisplayType.Danger,
          true
        );
      } else {
        let message = "";
        if (errors.codes?.includes(VoucherConstants.VoucherSumNotZero)) {
          message = TranslationKeys.Voucher.NotSumToZero;
        } else if (errors.codes?.includes(VoucherConstants.AccountLocked)) {
          message = TranslationKeys.Voucher.HasLockedAccounts;
        } else if (errors.codes?.includes(VoucherConstants.PeriodLocked)) {
          message = TranslationKeys.Voucher.PeriodLocked;
        } else if (errors.codes?.includes(VoucherConstants.AccountInactive)) {
          message = TranslationKeys.Voucher.ContainsInactiveAccounts;
        } else if (errors?.message && errors.message.length > 0) {
          message = errors?.message;
        } else {
          message = TranslationKeys.Errors.PleaseContactSupport;
        }

        enqueueMessage(LocalizationHelperInstance.getFormattedMessage(message), StyleEnums.DisplayType.Danger, true);
      }
    }
    throw errors;
  };

  const onItemSelect = async (
    item: AutocompleteListItem,
    name: string,
    formikProps: FormikProps<VoucherFormTypes.Create>
  ) => {
    const { values, setFieldValue } = formikProps;

    const balance = await accountService.getAccountBalance(Number(item.data));

    const balanceAccounts = [...(values.accountBalances ?? [])];
    const newBA = [
      ...balanceAccounts,
      { accountId: item.id, balanceBefore: balance?.balance } as VoucherFormTypes.BalaceAccountItem,
    ];
    setFieldValue(FormFieldConstants.Voucher.Create.AccountBalances, newBA);
  };

  const onAddNewRow = (formikProps: FormikProps<VoucherFormTypes.Create>) => {
    const { setFieldValue, values } = formikProps;

    const diff = values.accountItems.map((i) => i.amount).reduce((a, b) => (a ?? 0) + (b ?? 0), 0) ?? 0;
    const rowIndex = values.accountItems.length;
    if (rowIndex > 0) {
      setFieldValue(
        `${FormFieldConstants.Voucher.Create.AccountItems}[${rowIndex}].${FormFieldConstants.Voucher.AccountItem.Amount}`,
        -diff
      );
    }
  };

  const filterAccounts = (str?: string) => {
    const filteredAccounts: AutocompleteListItem[] = accounts
      ?.filter((item) => {
        return str === undefined || str?.length === 0 || item.number.toString().startsWith(str ?? "");
      })
      .map((item) => {
        return {
          id: item.id,
          value: `${item.number.toString()} (${item.name})`,
          description: item.name,
          data: item.number,
        };
      });
    return filteredAccounts;
  };

  return (
    <>
      {loading && (
        <div className={cssExports.dialogLoadingContainer}>
          <LoadingWithLogo />
        </div>
      )}
      <VoucherCreateForm
        ref={innerRef}
        {...props}
        defaultValues={initialValues}
        validationSchema={validationSchema}
        handleSubmit={handleSubmit}
        dimensionChangeHandler={onDimensionChange}
        itemChangeHandler={onItemAmountChange}
        totalChangeHandler={onTotalChange}
        accounts={accounts}
        onItemSelect={onItemSelect}
        onAddNewRow={onAddNewRow}
        filterAccounts={filterAccounts}
      />
      <Portal container={footerMount}>
        <Grid className={cssExports.cashReconciliationCreateFooter}>
          <label>{LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Voucher.Difference)}</label>
          <label className={clsx(cssExports.footerValue, cashDifference !== 0 ? cssExports.danger : "")}>
            {LocalizationHelperInstance.getFormattedCurrency(cashDifference, CurrencyEnums.CurrencyCode.SEK)}
          </label>
        </Grid>
      </Portal>
    </>
  );
};

export default React.forwardRef(VoucherCreateFormContainer);
